import React, { useState } from "react";
import "./EHRViewer.css";

/**
 * EHR Viewer Component
 *
 * Displays Electronic Health Records data in a structured panel view.
 * Supports JSON data with nested objects and arrays.
 */
const EHRViewer = ({ ehrData, onClose }) => {
    const [expandedSections, setExpandedSections] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("structured"); // structured, raw

    const toggleSection = (key) => {
        setExpandedSections(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const expandAll = () => {
        const allExpanded = {};
        const setAllExpanded = (obj, prefix = '') => {
            if (typeof obj === 'object' && obj !== null) {
                Object.keys(obj).forEach(key => {
                    const fullKey = prefix ? `${prefix}.${key}` : key;
                    allExpanded[fullKey] = true;
                    if (typeof obj[key] === 'object') {
                        setAllExpanded(obj[key], fullKey);
                    }
                });
            }
        };
        setAllExpanded(ehrData.content);
        setExpandedSections(allExpanded);
    };

    const collapseAll = () => {
        setExpandedSections({});
    };

    const formatValue = (value) => {
        if (value === null) return <span className="ehr-null">null</span>;
        if (value === undefined) return <span className="ehr-undefined">undefined</span>;
        if (typeof value === 'boolean') return <span className={`ehr-boolean ${value ? 'true' : 'false'}`}>{value.toString()}</span>;
        if (typeof value === 'number') return <span className="ehr-number">{value}</span>;
        if (typeof value === 'string') {
            // Check if it looks like a date
            if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
                return <span className="ehr-date">{value}</span>;
            }
            // Check if it's a URL
            if (value.startsWith('http://') || value.startsWith('https://')) {
                return <a href={value} target="_blank" rel="noopener noreferrer" className="ehr-url">{value}</a>;
            }
            return <span className="ehr-string">"{value}"</span>;
        }
        return String(value);
    };

    const renderValue = (key, value, depth = 0, parentKey = '') => {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const isExpanded = expandedSections[fullKey] ?? depth < 2; // Auto-expand first 2 levels

        // Filter by search term
        const matchesSearch = !searchTerm ||
            key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase()));

        if (typeof value === 'object' && value !== null) {
            const isArray = Array.isArray(value);
            const itemCount = isArray ? value.length : Object.keys(value).length;

            // Check if any child matches search
            const hasMatchingChild = searchTerm && JSON.stringify(value).toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch && !hasMatchingChild && searchTerm) {
                return null;
            }

            return (
                <div key={fullKey} className={`ehr-item ehr-object depth-${Math.min(depth, 4)}`}>
                    <div
                        className="ehr-key-row"
                        onClick={() => toggleSection(fullKey)}
                    >
                        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} ehr-chevron`}></i>
                        <span className="ehr-key">{key}</span>
                        <span className="ehr-type">
                            {isArray ? `Array[${itemCount}]` : `Object{${itemCount}}`}
                        </span>
                    </div>
                    {isExpanded && (
                        <div className="ehr-nested">
                            {isArray ? (
                                value.map((item, index) => renderValue(index, item, depth + 1, fullKey))
                            ) : (
                                Object.entries(value).map(([k, v]) => renderValue(k, v, depth + 1, fullKey))
                            )}
                        </div>
                    )}
                </div>
            );
        }

        if (!matchesSearch && searchTerm) {
            return null;
        }

        return (
            <div key={fullKey} className={`ehr-item ehr-primitive depth-${Math.min(depth, 4)}`}>
                <span className="ehr-key">{key}:</span>
                {formatValue(value)}
            </div>
        );
    };

    const content = ehrData.content || {};

    return (
        <div className="ehr-viewer-overlay" onClick={onClose}>
            <div className="ehr-viewer-panel" onClick={(e) => e.stopPropagation()}>
                <div className="ehr-viewer-header">
                    <div className="ehr-viewer-title">
                        <i className="fa fa-heartbeat me-2"></i>
                        <span>EHR Viewer</span>
                        {ehrData.filename && (
                            <span className="ehr-filename ms-2">- {ehrData.filename}</span>
                        )}
                    </div>
                    <div className="ehr-viewer-actions">
                        <div className="btn-group btn-group-sm me-2">
                            <button
                                className={`btn ${viewMode === 'structured' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setViewMode('structured')}
                            >
                                <i className="fa fa-sitemap me-1"></i> Structured
                            </button>
                            <button
                                className={`btn ${viewMode === 'raw' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setViewMode('raw')}
                            >
                                <i className="fa fa-code me-1"></i> Raw JSON
                            </button>
                        </div>
                        <button className="ehr-close-btn" onClick={onClose}>
                            <i className="fa fa-times"></i>
                        </button>
                    </div>
                </div>

                <div className="ehr-viewer-toolbar">
                    <div className="ehr-search">
                        <i className="fa fa-search"></i>
                        <input
                            type="text"
                            placeholder="Search EHR data..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="ehr-clear-search" onClick={() => setSearchTerm('')}>
                                <i className="fa fa-times"></i>
                            </button>
                        )}
                    </div>
                    {viewMode === 'structured' && (
                        <div className="ehr-expand-controls">
                            <button className="btn btn-sm btn-outline-secondary" onClick={expandAll}>
                                <i className="fa fa-expand me-1"></i> Expand All
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={collapseAll}>
                                <i className="fa fa-compress me-1"></i> Collapse All
                            </button>
                        </div>
                    )}
                </div>

                <div className="ehr-viewer-content">
                    {viewMode === 'structured' ? (
                        <div className="ehr-structured-view">
                            {Object.keys(content).length === 0 ? (
                                <div className="ehr-empty">
                                    <i className="fa fa-info-circle"></i>
                                    <p>No EHR data available</p>
                                </div>
                            ) : (
                                Object.entries(content).map(([key, value]) =>
                                    renderValue(key, value, 0, '')
                                )
                            )}
                        </div>
                    ) : (
                        <div className="ehr-raw-view">
                            <pre>{JSON.stringify(content, null, 2)}</pre>
                        </div>
                    )}
                </div>

                <div className="ehr-viewer-footer">
                    <div className="ehr-meta">
                        {ehrData.patient_id && (
                            <span className="ehr-meta-item">
                                <i className="fa fa-user me-1"></i>
                                Patient: {ehrData.patient_id}
                            </span>
                        )}
                        {ehrData.uploaded_at && (
                            <span className="ehr-meta-item">
                                <i className="fa fa-clock-o me-1"></i>
                                Uploaded: {new Date(ehrData.uploaded_at).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                    <button className="btn btn-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EHRViewer;
